import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Gift, Users, Crown, BookOpen } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export const ReferralPanel = () => {
  const { data } = useQuery({
    queryKey: ["referrals"],
    queryFn: async () => (await api.get("/account/referrals")).data,
  });
  if (!data) return <p className="text-gray-500 py-10 font-mono text-sm">Loading…</p>;
  const link = `${window.location.origin}/signup?ref=${data.referral_code}`;
  const copy = async () => {
    await navigator.clipboard.writeText(link);
    toast.success("Invite link copied — share it anywhere");
  };
  const stats = [
    { icon: Users, label: "Friends invited", value: data.invited },
    { icon: BookOpen, label: "Activated", value: data.activated },
    { icon: Crown, label: "Months earned", value: data.months_earned },
  ];
  return (
    <div data-testid="referral-panel" className="space-y-6 max-w-2xl">
      <div className="bg-[#1a1505] border border-[#F59E0B]/30 rounded-xl p-6">
        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#F59E0B] mb-3">
          <Gift className="h-4 w-4" /> Invite friends, earn Premium
        </div>
        <p className="text-gray-300 text-sm leading-relaxed">
          Share your personal link. When a friend signs up and reads <span className="text-white font-semibold">3 stories</span>,
          you get <span className="text-white font-semibold">30 days of NewsLayer Premium</span> — free. No limit.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <code data-testid="referral-link" className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-md px-3 py-2.5 font-mono text-xs text-[#00E5FF] truncate">
            {link}
          </code>
          <Button onClick={copy} data-testid="referral-copy-button"
            className="bg-[#F59E0B] text-black font-bold hover:bg-[#d98b09] shrink-0">
            <Copy className="h-4 w-4 mr-1.5" /> Copy link
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-[#121212] border border-white/10 rounded-lg p-4 text-center">
            <s.icon className="h-4 w-4 text-[#00E5FF] mx-auto mb-2" />
            <p className="font-mono text-2xl font-bold text-white">{s.value}</p>
            <p className="font-mono text-[9px] uppercase tracking-widest text-gray-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {data.is_premium && data.premium_until && (
        <p data-testid="referral-premium-until" className="font-mono text-xs text-[#F59E0B]">
          <Crown className="h-3.5 w-3.5 inline mr-1" />
          Premium active until {new Date(data.premium_until).toLocaleDateString()}
        </p>
      )}

      {(data.referrals || []).length > 0 && (
        <div>
          <h3 className="font-heading font-bold text-white text-base mb-3">Your invites</h3>
          <div className="space-y-2">
            {data.referrals.map((r: any, i: number) => (
              <div key={i} className="flex items-center justify-between bg-[#121212] border border-white/10 rounded-lg px-4 py-2.5">
                <div>
                  <p className="font-mono text-sm text-white">{r.email}</p>
                  <p className="font-mono text-[10px] text-gray-500 mt-0.5">{timeAgo(r.created_at)}</p>
                </div>
                <Badge variant="outline" className={`font-mono text-[10px] uppercase ${
                  r.status === "credited"
                    ? "bg-green-500/15 text-green-400 border-green-500/30"
                    : "bg-yellow-500/15 text-yellow-400 border-yellow-500/30"}`}>
                  {r.status === "credited" ? "credited" : "reading (needs 3 stories)"}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
